// HT/FT (Phase 3 → M2 wave 5). Picks the view (flow default / bars) and computes
// the shared counts once. The view is toggled by a legacy button that flips
// `mod.dataset.htftView` and calls refreshModuleBody — the value arrives as
// `view`. Flipping views unmounts one child and mounts the other (matching the
// legacy rebuild); the bars child is keyed by fixture+team so those changes
// reset its fills. Data now comes from app-api via `useWidgetData` (was the
// `getFormData("recent")` legacyData getter — the recent-form slice); the
// `ht-ft` endpoint returns that FormData. Counts + windowing stay pure
// (@bento/shared); team + timeline reshape client-side, no refetch.

import { htftCounts, selectHtftMatches } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import HtFtFlow from "./HtFtFlow.js";
import HtFtBars from "./HtFtBars.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface HtFtProps {
  fixture: string;
  team?: string | null;
  timeline?: string | null;
  /** "flow" (default) | "bars". */
  view?: string | null;
}

export default function HtFt({ fixture, team, timeline, view }: HtFtProps) {
  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const chosen = team || (parts[0] ?? "").trim();
  const { data, isPending, isError, refetch } = useWidgetData(
    "ht-ft",
    useFixtureKey(fixture)
  );

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const src = data?.[chosen] ?? [];
  const matches = selectHtftMatches(src, timeline);
  const counts = htftCounts(matches, chosen);
  const total = matches.length || 1;

  if (view === "bars") {
    return <HtFtBars key={`${fixture}|${chosen}`} counts={counts} total={total} />;
  }
  return <HtFtFlow counts={counts} total={total} />;
}
